import { Temporal } from "@js-temporal/polyfill";
import {
  Body,
  Equator,
  Horizon,
  Illumination,
  Observer,
  SearchHourAngle,
  SearchRiseSet,
  Seasons,
} from "astronomy-engine";

export type SolarSeason = "summer" | "winter" | "custom";

export interface SolarLocation {
  label: string;
  lat: number;
  lon: number;
  elevationM: number;
  timeZone: string;
}

export interface SolarVector {
  x: number;
  y: number;
  z: number;
}

export interface CelestialPosition {
  azimuthDeg: number;
  elevationDeg: number;
  aboveHorizon: boolean;
  vector: SolarVector;
}

export interface SolarEvent {
  instantISO: string;
  localTimeLabel: string;
}

export interface SolarDayEvents {
  sunrise: SolarEvent | null;
  solarNoon: SolarEvent | null;
  sunset: SolarEvent | null;
  horizonCondition: "normal" | "midnight-sun" | "polar-night";
}

export interface SolarDisplay {
  dateLabel: string;
  timeLabel: string;
  requestedTimeLabel: string;
  zoneLabel: string;
  sunAzimuth: string;
  sunElevation: string;
  moonAzimuth: string;
  moonElevation: string;
  sunlightStatus: string;
}

export interface SolarStudyState {
  enabled: boolean;
  location: SolarLocation;
  season: SolarSeason;
  date: string;
  minuteIndex: number;
  dayLengthMinutes: number;
  playing: boolean;
  /** Shared 0–1 surface snow depth; playback builds it in winter and melts it in summer. */
  snowCoverage: number;
  facadeBearing: number;
  overhangM: number;
  instantISO: string;
  offset: string;
  dstAdjusted: boolean;
  sun: CelestialPosition;
  moon: CelestialPosition & { illuminatedFraction: number };
  events: SolarDayEvents;
  display: SolarDisplay;
}

export interface LocationSearchResult extends SolarLocation {
  id: number;
  country?: string;
  admin1?: string;
}

export type SolarStudyPatch = Partial<
  Pick<
    SolarStudyState,
    | "enabled"
    | "season"
    | "date"
    | "minuteIndex"
    | "playing"
    | "snowCoverage"
    | "facadeBearing"
    | "overhangM"
  >
> & { location?: Partial<SolarLocation> };

export type SolarStudySubscriber = (state: SolarStudyState) => void;

export const NEDERLAND_LOCATION: Readonly<SolarLocation> = Object.freeze({
  label: "Nederland, Colorado",
  lat: 39.9633205,
  lon: -105.5071871,
  elevationM: 2510,
  timeZone: "America/Denver",
});

const RAD = Math.PI / 180;
const PLAYBACK_INTERVAL_MS = 80;
const PLAYBACK_STEP_MINUTES = 2;
const eventCache = new Map<string, SolarDayEvents>();

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function advanceSnowCoverage(
  coverage: number,
  season: SolarSeason,
  simulatedMinutes: number,
): number {
  const winter = season === "winter";
  const transitionMinutes = winter ? 360 : 180;
  return clamp(
    finiteNumber(coverage, 0)
      + (winter ? 1 : -1) * Math.max(0, finiteNumber(simulatedMinutes, 0)) / transitionMinutes,
    0,
    1,
  );
}

function finiteNumber(value: unknown, fallback: number): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

export function normalizeBearing(value: number): number {
  return ((value % 360) + 360) % 360;
}

export function equatorFacingBearing(latitude: number): 0 | 180 {
  return finiteNumber(latitude, 0) < 0 ? 0 : 180;
}

function bearingsMatch(first: number, second: number): boolean {
  const difference = Math.abs(normalizeBearing(first) - normalizeBearing(second));
  return Math.min(difference, 360 - difference) < 0.000001;
}

function validateTimeZone(timeZone: string): string {
  const candidate = String(timeZone || "").trim();
  if (!candidate) throw new RangeError("An IANA time zone is required.");
  Temporal.Now.zonedDateTimeISO(candidate);
  return candidate;
}

function normalizeDate(date: string): string {
  return Temporal.PlainDate.from(date).toString();
}

function normalizeLocation(
  next: Partial<SolarLocation>,
  fallback: SolarLocation = NEDERLAND_LOCATION,
): SolarLocation {
  const lat = finiteNumber(next.lat, fallback.lat);
  const lon = finiteNumber(next.lon, fallback.lon);
  const elevationM = finiteNumber(next.elevationM, fallback.elevationM);
  if (lat < -90 || lat > 90) throw new RangeError("Latitude must be between -90 and 90 degrees.");
  if (lon < -180 || lon > 180) throw new RangeError("Longitude must be between -180 and 180 degrees.");
  if (elevationM < -500 || elevationM > 100000) {
    throw new RangeError("Elevation must be between -500 and 100,000 meters.");
  }
  return {
    label: String(next.label ?? fallback.label).trim() || "Custom location",
    lat,
    lon,
    elevationM,
    timeZone: validateTimeZone(String(next.timeZone ?? fallback.timeZone)),
  };
}

function astroDateToLocalDate(date: Date, timeZone: string): string {
  return Temporal.Instant.fromEpochMilliseconds(date.getTime())
    .toZonedDateTimeISO(timeZone)
    .toPlainDate()
    .toString();
}

/** Returns the local calendar date of the astronomical summer or winter solstice. */
export function getSeasonDate(
  year: number,
  season: Exclude<SolarSeason, "custom">,
  latitude: number,
  timeZone: string,
): string {
  const events = Seasons(Math.trunc(year));
  const northernHemisphere = latitude >= 0;
  const useJune = season === "summer" ? northernHemisphere : !northernHemisphere;
  const event = useJune ? events.jun_solstice : events.dec_solstice;
  return astroDateToLocalDate(event.date, validateTimeZone(timeZone));
}

export interface ResolvedLocalTime {
  zonedDateTime: Temporal.ZonedDateTime;
  requestedTimeLabel: string;
  dstAdjusted: boolean;
  dayLengthMinutes: number;
}

/**
 * Builds a real elapsed-minute timeline from one site-local midnight to the
 * next. It contains 1380 minutes on a spring-forward day, 1500 on a fall-back
 * day, and preserves both occurrences of a repeated fall-back clock minute.
 */
export function getLocalDayTimeline(date: string, timeZone: string): {
  start: Temporal.ZonedDateTime;
  end: Temporal.ZonedDateTime;
  dayLengthMinutes: number;
} {
  const start = Temporal.PlainDate.from(normalizeDate(date)).toZonedDateTime({
    timeZone: validateTimeZone(timeZone),
    plainTime: Temporal.PlainTime.from("00:00"),
  });
  const end = start.add({ days: 1 });
  const dayLengthMinutes = Math.round(
    Number(end.epochMilliseconds - start.epochMilliseconds) / 60_000,
  );
  return { start, end, dayLengthMinutes };
}

export function resolveLocalTime(
  date: string,
  minuteIndex: number,
  timeZone: string,
): ResolvedLocalTime {
  const timeline = getLocalDayTimeline(date, timeZone);
  const minute = Math.round(clamp(
    finiteNumber(minuteIndex, 0),
    0,
    timeline.dayLengthMinutes - 1,
  ));
  const zonedDateTime = timeline.start.add({ minutes: minute });
  const requestedTimeLabel = `${String(zonedDateTime.hour).padStart(2, "0")}:${String(zonedDateTime.minute).padStart(2, "0")}`;
  return {
    zonedDateTime,
    requestedTimeLabel,
    dstAdjusted: timeline.dayLengthMinutes !== 1440,
    dayLengthMinutes: timeline.dayLengthMinutes,
  };
}

export function celestialVector(
  azimuthDeg: number,
  elevationDeg: number,
  facadeBearing: number,
): SolarVector {
  const elevation = elevationDeg * RAD;
  const delta = (azimuthDeg - normalizeBearing(facadeBearing)) * RAD;
  const horizontal = Math.cos(elevation);
  return {
    x: -horizontal * Math.sin(delta),
    y: Math.sin(elevation),
    z: horizontal * Math.cos(delta),
  };
}

function bodyPosition(
  body: Body,
  instant: Date,
  location: SolarLocation,
  facadeBearing: number,
): CelestialPosition {
  const observer = new Observer(location.lat, location.lon, location.elevationM);
  const equatorial = Equator(body, instant, observer, true, true);
  // Shadows use the geometric direction. Refraction is intentionally omitted;
  // it only belongs in the human-visible rise/set event convention below.
  const horizontal = Horizon(instant, observer, equatorial.ra, equatorial.dec);
  return {
    azimuthDeg: horizontal.azimuth,
    elevationDeg: horizontal.altitude,
    aboveHorizon: horizontal.altitude > 0,
    vector: celestialVector(horizontal.azimuth, horizontal.altitude, facadeBearing),
  };
}

function instantFromEpochMilliseconds(epochMilliseconds: number): string {
  return Temporal.Instant.fromEpochMilliseconds(epochMilliseconds).toString();
}

function formatEvent(date: Date, timeZone: string): SolarEvent {
  return Object.freeze({
    instantISO: instantFromEpochMilliseconds(date.getTime()),
    localTimeLabel: new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(date),
  });
}

function eventOnDate(
  date: Date | null,
  startEpochMs: number,
  endEpochMs: number,
  timeZone: string,
): SolarEvent | null {
  if (!date) return null;
  const epochMs = date.getTime();
  return epochMs >= startEpochMs && epochMs < endEpochMs ? formatEvent(date, timeZone) : null;
}

export function calculateDayEvents(date: string, location: SolarLocation): SolarDayEvents {
  const normalizedDate = normalizeDate(date);
  const cacheKey = [
    normalizedDate,
    location.lat.toFixed(7),
    location.lon.toFixed(7),
    location.elevationM.toFixed(1),
    location.timeZone,
  ].join("|");
  const cached = eventCache.get(cacheKey);
  if (cached) return cached;

  const { start, end } = getLocalDayTimeline(normalizedDate, location.timeZone);
  const startEpochMs = Number(start.epochMilliseconds);
  const endEpochMs = Number(end.epochMilliseconds);
  const startDate = new Date(startEpochMs);
  const searchDays = (endEpochMs - startEpochMs) / 86_400_000 + 0.1;
  const observer = new Observer(location.lat, location.lon, location.elevationM);

  const rise = SearchRiseSet(Body.Sun, observer, +1, startDate, searchDays)?.date ?? null;
  const set = SearchRiseSet(Body.Sun, observer, -1, startDate, searchDays)?.date ?? null;
  const transit = SearchHourAngle(Body.Sun, observer, 0, startDate, +1).time.date;
  const sunrise = eventOnDate(rise, startEpochMs, endEpochMs, location.timeZone);
  const sunset = eventOnDate(set, startEpochMs, endEpochMs, location.timeZone);
  const solarNoon = eventOnDate(transit, startEpochMs, endEpochMs, location.timeZone);

  let horizonCondition: SolarDayEvents["horizonCondition"] = "normal";
  if (!sunrise && !sunset) {
    const noonPosition = bodyPosition(
      Body.Sun,
      solarNoon ? new Date(solarNoon.instantISO) : new Date((startEpochMs + endEpochMs) / 2),
      location,
      180,
    );
    horizonCondition = noonPosition.elevationDeg > 0 ? "midnight-sun" : "polar-night";
  }

  const result = Object.freeze({ sunrise, solarNoon, sunset, horizonCondition });
  eventCache.set(cacheKey, result);
  return result;
}

function directionName(azimuth: number): string {
  const names = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return names[Math.round(normalizeBearing(azimuth) / 45) % names.length];
}

function formatDegrees(value: number): string {
  return `${value.toFixed(1)}°`;
}

interface BaseSolarState {
  enabled: boolean;
  location: SolarLocation;
  season: SolarSeason;
  date: string;
  minuteIndex: number;
  playing: boolean;
  snowCoverage?: number;
  facadeBearing: number;
  overhangM: number;
}

export function calculateSolarState(base: BaseSolarState): SolarStudyState {
  const location = normalizeLocation(base.location);
  const date = normalizeDate(base.date);
  const timeline = getLocalDayTimeline(date, location.timeZone);
  const minuteIndex = Math.round(clamp(
    finiteNumber(base.minuteIndex, 0),
    0,
    timeline.dayLengthMinutes - 1,
  ));
  const facadeBearing = normalizeBearing(finiteNumber(base.facadeBearing, 180));
  const overhangM = clamp(finiteNumber(base.overhangM, 0), 0, 1.5);
  const snowCoverage = clamp(finiteNumber(base.snowCoverage, 0), 0, 1);
  const resolved = resolveLocalTime(date, minuteIndex, location.timeZone);
  const epochMs = Number(resolved.zonedDateTime.epochMilliseconds);
  const instant = new Date(epochMs);
  const sun = bodyPosition(Body.Sun, instant, location, facadeBearing);
  const moonPosition = bodyPosition(Body.Moon, instant, location, facadeBearing);
  const illuminatedFraction = Illumination(Body.Moon, instant).phase_fraction;
  const events = calculateDayEvents(date, location);
  const formatterOptions = { timeZone: location.timeZone } as const;
  const timeLabel = new Intl.DateTimeFormat("en-US", {
    ...formatterOptions,
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(instant);
  const dateLabel = new Intl.DateTimeFormat("en-US", {
    ...formatterOptions,
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(instant);
  const zoneLabel = new Intl.DateTimeFormat("en-US", {
    ...formatterOptions,
    timeZoneName: "long",
  })
    .formatToParts(instant)
    .find((part) => part.type === "timeZoneName")?.value ?? location.timeZone;
  const sunlightStatus = sun.elevationDeg > 0
    ? "Direct sun above the flat horizon"
    : sun.elevationDeg > -6
      ? "Civil twilight — no direct sun"
      : "Sun below the flat horizon";

  return Object.freeze({
    enabled: Boolean(base.enabled),
    location: Object.freeze(location),
    season: base.season,
    date,
    minuteIndex,
    dayLengthMinutes: resolved.dayLengthMinutes,
    playing: Boolean(base.playing),
    snowCoverage,
    facadeBearing,
    overhangM,
    instantISO: instantFromEpochMilliseconds(epochMs),
    offset: resolved.zonedDateTime.offset,
    dstAdjusted: resolved.dstAdjusted,
    sun: Object.freeze({ ...sun, vector: Object.freeze(sun.vector) }),
    moon: Object.freeze({
      ...moonPosition,
      vector: Object.freeze(moonPosition.vector),
      illuminatedFraction,
    }),
    events,
    display: Object.freeze({
      dateLabel,
      timeLabel,
      requestedTimeLabel: resolved.requestedTimeLabel,
      zoneLabel,
      sunAzimuth: `${formatDegrees(sun.azimuthDeg)} ${directionName(sun.azimuthDeg)}`,
      sunElevation: formatDegrees(sun.elevationDeg),
      moonAzimuth: `${formatDegrees(moonPosition.azimuthDeg)} ${directionName(moonPosition.azimuthDeg)}`,
      moonElevation: formatDegrees(moonPosition.elevationDeg),
      sunlightStatus,
    }),
  });
}

function defaultBaseState(): BaseSolarState {
  const location = { ...NEDERLAND_LOCATION };
  const currentYear = Temporal.Now.zonedDateTimeISO(location.timeZone).year;
  return {
    enabled: true,
    location,
    season: "summer",
    date: getSeasonDate(currentYear, "summer", location.lat, location.timeZone),
    minuteIndex: 12 * 60,
    // The study is ready at noon, but waits for the user to reach the
    // interactive pan/spin plateau before starting its day cycle.
    playing: false,
    snowCoverage: 0,
    facadeBearing: 180,
    overhangM: 0,
  };
}

class SolarStudyStore {
  private base: BaseSolarState = defaultBaseState();
  private state: SolarStudyState = calculateSolarState(this.base);
  private subscribers = new Set<SolarStudySubscriber>();
  private playbackTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.syncPlayback();
  }

  getState = (): SolarStudyState => this.state;

  subscribe = (subscriber: SolarStudySubscriber): (() => void) => {
    if (typeof subscriber !== "function") throw new TypeError("subscribe requires a callback.");
    this.subscribers.add(subscriber);
    subscriber(this.state);
    return () => this.subscribers.delete(subscriber);
  };

  patchState = (patch: SolarStudyPatch): SolarStudyState => {
    const previous = this.base;
    const location = patch.location
      ? normalizeLocation(patch.location, previous.location)
      : previous.location;
    if (patch.season !== undefined && !["summer", "winter", "custom"].includes(patch.season)) {
      throw new RangeError("Season must be summer, winter, or custom.");
    }
    const nextSeason = patch.season ?? previous.season;
    let nextDate = patch.date ? normalizeDate(patch.date) : previous.date;
    let effectiveSeason = patch.date && patch.season === undefined ? "custom" as const : nextSeason;
    const locationChanged = location !== previous.location;
    const seasonChanged = patch.season !== undefined && patch.season !== previous.season;
    if (effectiveSeason !== "custom" && (seasonChanged || locationChanged)) {
      const year = Temporal.PlainDate.from(nextDate).year;
      nextDate = getSeasonDate(year, effectiveSeason, location.lat, location.timeZone);
    }

    const facadeBearing = patch.facadeBearing === undefined
      ? locationChanged && bearingsMatch(
        previous.facadeBearing,
        equatorFacingBearing(previous.location.lat),
      )
        ? equatorFacingBearing(location.lat)
        : previous.facadeBearing
      : normalizeBearing(finiteNumber(patch.facadeBearing, previous.facadeBearing));

    this.base = {
      enabled: patch.enabled === undefined ? previous.enabled : Boolean(patch.enabled),
      location,
      season: effectiveSeason,
      date: nextDate,
      minuteIndex: Math.round(clamp(
        finiteNumber(patch.minuteIndex ?? previous.minuteIndex, previous.minuteIndex),
        0,
        getLocalDayTimeline(nextDate, location.timeZone).dayLengthMinutes - 1,
      )),
      playing: patch.playing === undefined ? previous.playing : Boolean(patch.playing),
      snowCoverage: patch.snowCoverage === undefined
        ? (previous.snowCoverage ?? 0)
        : clamp(finiteNumber(patch.snowCoverage, previous.snowCoverage ?? 0), 0, 1),
      facadeBearing,
      overhangM: patch.overhangM === undefined
        ? previous.overhangM
        : clamp(finiteNumber(patch.overhangM, previous.overhangM), 0, 1.5),
    };
    this.state = calculateSolarState(this.base);
    this.syncPlayback();
    this.notify();
    return this.state;
  };

  setEnabled = (enabled: boolean): SolarStudyState => this.patchState({ enabled });

  setLocation = (location: SolarLocation): SolarStudyState => this.patchState({ location });

  setSeason = (season: SolarSeason): SolarStudyState => this.patchState({ season });

  setDate = (date: string): SolarStudyState => this.patchState({ date });

  setMinuteIndex = (minuteIndex: number): SolarStudyState => this.patchState({ minuteIndex });

  setPlaying = (playing: boolean): SolarStudyState => this.patchState({ playing });

  togglePlaying = (): SolarStudyState => this.patchState({ playing: !this.base.playing });

  setFacadeBearing = (facadeBearing: number): SolarStudyState => this.patchState({ facadeBearing });

  setOverhangM = (overhangM: number): SolarStudyState => this.patchState({ overhangM });

  private notify(): void {
    for (const subscriber of [...this.subscribers]) subscriber(this.state);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("quonset-solar-change", { detail: this.state }));
    }
  }

  private syncPlayback(): void {
    if (!this.base.playing && this.playbackTimer !== null) {
      clearInterval(this.playbackTimer);
      this.playbackTimer = null;
      return;
    }
    if (this.base.playing && this.playbackTimer === null && typeof window !== "undefined") {
      this.playbackTimer = setInterval(() => {
        const minuteIndex = (this.base.minuteIndex + PLAYBACK_STEP_MINUTES)
          % this.state.dayLengthMinutes;
        const snowCoverage = advanceSnowCoverage(
          this.base.snowCoverage ?? 0,
          this.base.season,
          PLAYBACK_STEP_MINUTES,
        );
        this.patchState({ minuteIndex, snowCoverage });
      }, PLAYBACK_INTERVAL_MS);
    }
  }
}

export const solarStudy = new SolarStudyStore();

export async function searchLocations(
  query: string,
  options: { count?: number; language?: string; signal?: AbortSignal } = {},
): Promise<LocationSearchResult[]> {
  const name = String(query).trim();
  if (name.length < 2) return [];
  const count = Math.round(clamp(options.count ?? 5, 1, 10));
  const language = String(options.language ?? "en").slice(0, 8);
  const params = new URLSearchParams({ name, count: String(count), language, format: "json" });
  const response = await fetch(`https://geocoding-api.open-meteo.com/v1/search?${params}`, {
    signal: options.signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Location search failed (${response.status}).`);
  const payload = await response.json() as {
    results?: Array<{
      id: number;
      name: string;
      latitude: number;
      longitude: number;
      elevation?: number;
      timezone?: string;
      country?: string;
      admin1?: string;
    }>;
  };
  return (payload.results ?? [])
    .filter((result) => result.timezone && Number.isFinite(result.latitude) && Number.isFinite(result.longitude))
    .map((result) => {
      const placeParts = [result.name, result.admin1, result.country].filter(Boolean);
      return {
        id: result.id,
        label: [...new Set(placeParts)].join(", "),
        lat: result.latitude,
        lon: result.longitude,
        elevationM: finiteNumber(result.elevation, 0),
        timeZone: result.timezone!,
        country: result.country,
        admin1: result.admin1,
      };
    });
}

export interface QuonsetSolarSceneOptions {
  THREE: any;
  modelId: string;
  scene: any;
  building: any;
  renderer: any;
  centerZ: number;
  frontZ: number;
  radius: number;
  baseLights?: { hemi?: any; sun?: any; glowLights?: any[] };
  legacyLights?: { hemi?: any; sun?: any; glowLights?: any[] };
  materials?: any[];
  groundMaterial?: any;
  glassMaterials?: any[];
  /** Ground-like surfaces that receive shadows but must not obstruct sunlight. */
  receiverOnlyMaterials?: any[];
  /** Separate, receiver-only winter surfaces animated by the shared season. */
  snowCover?: {
    groundMeshes?: any[];
    canopyMeshes?: any[];
  };
  setLegacySkyVisible?: (visible: boolean) => void;
  restoreTheme?: () => void;
  requestRender?: () => void;
}

export interface QuonsetSolarSceneAdapter {
  frame(): void;
  refresh(): void;
  invalidate(): void;
  isEnabled(): boolean;
  dispose(): void;
}

interface ObjectShadowState {
  object: any;
  castShadow: boolean;
  receiveShadow: boolean;
}

interface MaterialShadowState {
  material: any;
  clipShadows: boolean;
}

function materialList(material: any): any[] {
  if (!material) return [];
  return Array.isArray(material) ? material.filter(Boolean) : [material];
}

function createCelestialMarker(THREE: any, color: number, radius: number): any {
  const geometry = new THREE.SphereGeometry(radius, 20, 12);
  const material = new THREE.MeshBasicMaterial({ color, fog: false, toneMapped: false });
  const marker = new THREE.Mesh(geometry, material);
  marker.frustumCulled = false;
  marker.renderOrder = 100;
  return marker;
}

function setDebugValue(modelId: string, value: Record<string, unknown> | null): void {
  if (typeof window === "undefined") return;
  const holder = (window.__quonsetSolarScenes ??= {});
  if (value) holder[modelId] = value;
  else delete holder[modelId];
  if (modelId === "studio") window.__qr3dSolar = value ?? undefined;
  if (modelId === "2br") window.__qr3d2brSolar = value ?? undefined;
}

export function attachQuonsetSolarScene(
  options: QuonsetSolarSceneOptions,
): QuonsetSolarSceneAdapter {
  const {
    THREE,
    modelId,
    building,
    renderer,
    centerZ,
    frontZ,
    radius,
    setLegacySkyVisible,
    restoreTheme,
    requestRender,
  } = options;
  if (!THREE || !building || !renderer) {
    throw new TypeError("attachQuonsetSolarScene requires THREE, building, and renderer.");
  }

  const baseLights = options.baseLights ?? options.legacyLights ?? {};
  const explicitMaterials = new Set((options.materials ?? []).filter(Boolean));
  const glassMaterials = new Set((options.glassMaterials ?? []).filter(Boolean));
  const receiverOnlyMaterials = new Set((options.receiverOnlyMaterials ?? []).filter(Boolean));
  const snowGroundMeshes = (options.snowCover?.groundMeshes ?? []).filter(Boolean);
  const snowCanopyMeshes = (options.snowCover?.canopyMeshes ?? []).filter(Boolean);
  const snowMeshes = [...snowGroundMeshes, ...snowCanopyMeshes];
  const snowMeshSet = new Set(snowMeshes);
  const snowMaterials = new Set<any>();
  const snowStates = snowMeshes.map((object: any) => {
    for (const material of materialList(object.material)) snowMaterials.add(material);
    return {
      object,
      visible: object.visible,
      scaleY: finiteNumber(object.scale?.y, 1),
      opacity: materialList(object.material).map((material) => finiteNumber(material?.opacity, 1)),
    };
  });
  const objectStates: ObjectShadowState[] = [];
  const materialStates: MaterialShadowState[] = [];
  const seenMaterials = new Set<any>();
  const savedLightIntensities = new Map<any, number>();
  for (const light of [baseLights.hemi, baseLights.sun, ...(baseLights.glowLights ?? [])]) {
    if (light && typeof light.intensity === "number") savedLightIntensities.set(light, light.intensity);
  }

  building.traverse((object: any) => {
    if (!object?.isMesh) return;
    objectStates.push({ object, castShadow: object.castShadow, receiveShadow: object.receiveShadow });
    const materials = materialList(object.material);
    const isReceiverOnly = snowMeshSet.has(object)
      || materials.includes(options.groundMaterial)
      || materials.some((material) => receiverOnlyMaterials.has(material));
    const isGlass = !isReceiverOnly && materials.some((material) =>
      glassMaterials.has(material)
      || (material?.transparent && finiteNumber(material.opacity, 1) < 0.98)
      || finiteNumber(material?.transmission, 0) > 0.01
    );
    object.castShadow = !isReceiverOnly && !isGlass;
    object.receiveShadow = !isGlass;
    for (const material of materials) {
      if (!material || seenMaterials.has(material)) continue;
      seenMaterials.add(material);
      materialStates.push({ material, clipShadows: Boolean(material.clipShadows) });
      if (explicitMaterials.size === 0 || explicitMaterials.has(material) || material.clippingPlanes?.length) {
        material.clipShadows = true;
        material.needsUpdate = true;
      }
    }
  });

  const previousShadow = {
    enabled: renderer.shadowMap.enabled,
    type: renderer.shadowMap.type,
    autoUpdate: renderer.shadowMap.autoUpdate,
  };
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;

  const rig = new THREE.Group();
  rig.name = `solar-study-${modelId}`;
  const target = new THREE.Object3D();
  target.name = `solar-target-${modelId}`;
  target.position.set(0, Math.max(1.2, radius * 0.34), centerZ);
  rig.add(target);

  const sunLight = new THREE.DirectionalLight(0xfff2d2, 0);
  sunLight.name = `physical-sun-${modelId}`;
  sunLight.castShadow = true;
  sunLight.target = target;
  const mobile = typeof matchMedia === "function" && matchMedia("(max-width: 720px)").matches;
  sunLight.shadow.mapSize.set(mobile ? 1024 : 2048, mobile ? 1024 : 2048);
  const shadowExtent = Math.max(10, Math.abs(frontZ - centerZ) + radius * 2.4);
  Object.assign(sunLight.shadow.camera, {
    left: -shadowExtent,
    right: shadowExtent,
    top: shadowExtent,
    bottom: -shadowExtent,
    near: 0.5,
    far: 160,
  });
  sunLight.shadow.bias = -0.00025;
  sunLight.shadow.normalBias = 0.025;
  sunLight.shadow.camera.updateProjectionMatrix?.();
  rig.add(sunLight);

  const moonLight = new THREE.DirectionalLight(0xa8c8ff, 0);
  moonLight.name = `physical-moon-${modelId}`;
  moonLight.castShadow = false;
  moonLight.target = target;
  rig.add(moonLight);

  const sunMarker = createCelestialMarker(THREE, 0xffd36a, 0.72);
  sunMarker.name = `sun-marker-${modelId}`;
  rig.add(sunMarker);
  const moonMarker = createCelestialMarker(THREE, 0xdce8ff, 0.48);
  moonMarker.name = `moon-marker-${modelId}`;
  rig.add(moonMarker);

  const overhangGeometry = new THREE.CylinderGeometry(
    radius + 0.08,
    radius + 0.08,
    1,
    64,
    1,
    true,
    -Math.PI / 2,
    Math.PI,
  );
  const overhangMaterial = new THREE.MeshStandardMaterial({
    color: 0x656b72,
    roughness: 0.82,
    metalness: 0.08,
    side: THREE.DoubleSide,
  });
  overhangMaterial.clipShadows = true;
  const overhang = new THREE.Mesh(overhangGeometry, overhangMaterial);
  overhang.name = `solar-overhang-${modelId}`;
  overhang.rotation.x = -Math.PI / 2;
  overhang.castShadow = true;
  overhang.receiveShadow = true;
  rig.add(overhang);
  building.add(rig);

  let enabled = false;
  let disposed = false;
  let restoringTheme = false;
  let shadowDirty = true;
  let lastShadowUpdate = -Infinity;
  let snowCoverage = 0;
  let lastAppliedSignature = "";
  const markerDistance = Math.max(28, shadowExtent * 1.8);
  const lightDistance = Math.max(55, shadowExtent * 3.2);
  const debug: Record<string, unknown> = { modelId, enabled: false };
  setDebugValue(modelId, debug);

  function applySnowCover(): void {
    const visible = snowCoverage > 0.002;
    const eased = snowCoverage * snowCoverage * (3 - 2 * snowCoverage);
    for (const mesh of snowGroundMeshes) mesh.visible = visible;
    for (const mesh of snowCanopyMeshes) {
      mesh.visible = visible;
      const fullScale = finiteNumber(mesh.userData?.snowFullScaleY, 1);
      mesh.scale.y = fullScale * (0.18 + eased * 0.82);
    }
    for (const material of snowMaterials) {
      material.opacity = eased * 0.96;
    }
    debug.snowCoverage = snowCoverage;
    debug.snowGroundCount = snowGroundMeshes.length;
    debug.snowCanopyCount = snowCanopyMeshes.length;
  }

  applySnowCover();

  function restoreBaseLights(): void {
    for (const [light, intensity] of savedLightIntensities) light.intensity = intensity;
  }

  function positionAlongVector(object: any, vector: SolarVector, distance: number): void {
    object.position.set(
      target.position.x + vector.x * distance,
      target.position.y + vector.y * distance,
      target.position.z + vector.z * distance,
    );
  }

  function refresh(): void {
    if (disposed || restoringTheme) return;
    const state = solarStudy.getState();
    const wasEnabled = enabled;
    enabled = state.enabled;
    const nextSnowCoverage = clamp(finiteNumber(state.snowCoverage, 0), 0, 1);
    if (Math.abs(nextSnowCoverage - snowCoverage) > 0.00001) {
      snowCoverage = nextSnowCoverage;
      applySnowCover();
    }
    const signature = [
      state.enabled,
      state.season,
      state.snowCoverage,
      state.instantISO,
      state.location.lat,
      state.location.lon,
      state.facadeBearing,
      state.overhangM,
    ].join("|");

    const overhangDepth = state.overhangM;
    overhang.visible = overhangDepth > 0.005;
    overhang.scale.set(1, Math.max(overhangDepth, 0.001), 1);
    overhang.position.set(0, 0, frontZ + overhangDepth / 2);

    positionAlongVector(sunLight, state.sun.vector, lightDistance);
    positionAlongVector(sunMarker, state.sun.vector, markerDistance);
    positionAlongVector(moonLight, state.moon.vector, lightDistance);
    positionAlongVector(moonMarker, state.moon.vector, markerDistance);

    sunLight.visible = enabled && state.sun.elevationDeg > 0;
    sunMarker.visible = enabled && state.sun.elevationDeg > -6;
    moonLight.visible = enabled && state.moon.elevationDeg > 0;
    moonMarker.visible = enabled && state.moon.elevationDeg > 0;
    const sunRamp = clamp(state.sun.elevationDeg / 12, 0, 1);
    sunLight.intensity = enabled ? 3.2 * sunRamp : 0;
    moonLight.intensity = enabled && state.sun.elevationDeg < -4
      ? 0.055 * state.moon.illuminatedFraction * clamp((state.moon.elevationDeg + 2) / 12, 0, 1)
      : 0;

    if (enabled) {
      if (baseLights.sun) baseLights.sun.intensity = 0;
      if (baseLights.hemi) {
        const savedHemi = savedLightIntensities.get(baseLights.hemi) ?? 0.65;
        baseLights.hemi.intensity = Math.min(savedHemi, 0.12 + 0.28 * sunRamp);
      }
      for (const glow of baseLights.glowLights ?? []) glow.intensity = 0;
    } else if (wasEnabled) {
      restoreBaseLights();
      if (restoreTheme) {
        restoringTheme = true;
        try {
          restoreTheme();
        } finally {
          restoringTheme = false;
        }
      }
    }

    setLegacySkyVisible?.(!enabled);
    if (signature !== lastAppliedSignature) {
      lastAppliedSignature = signature;
      shadowDirty = enabled;
      requestRender?.();
    }
    Object.assign(debug, {
      enabled,
      instantISO: state.instantISO,
      location: state.location.label,
      facadeBearing: state.facadeBearing,
      overhangM: state.overhangM,
      sunAzimuthDeg: state.sun.azimuthDeg,
      sunElevationDeg: state.sun.elevationDeg,
      sunVector: { ...state.sun.vector },
      moonAzimuthDeg: state.moon.azimuthDeg,
      moonElevationDeg: state.moon.elevationDeg,
      moonVector: { ...state.moon.vector },
      shadowDirty,
    });
  }

  function invalidate(): void {
    if (disposed) return;
    shadowDirty = enabled;
    requestRender?.();
  }

  function frame(): void {
    if (disposed || !enabled || !shadowDirty) return;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - lastShadowUpdate < 66) return;
    renderer.shadowMap.needsUpdate = true;
    shadowDirty = false;
    lastShadowUpdate = now;
    debug.shadowDirty = false;
    debug.lastShadowUpdate = now;
  }

  const unsubscribe = solarStudy.subscribe(refresh);

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    unsubscribe();
    if (enabled) restoreBaseLights();
    setLegacySkyVisible?.(true);
    building.remove(rig);
    for (const state of objectStates) {
      state.object.castShadow = state.castShadow;
      state.object.receiveShadow = state.receiveShadow;
    }
    for (const state of materialStates) {
      state.material.clipShadows = state.clipShadows;
      state.material.needsUpdate = true;
    }
    for (const state of snowStates) {
      state.object.visible = state.visible;
      state.object.scale.y = state.scaleY;
      materialList(state.object.material).forEach((material, index) => {
        material.opacity = state.opacity[index] ?? 1;
        material.needsUpdate = true;
      });
    }
    sunLight.shadow.map?.dispose?.();
    overhangGeometry.dispose();
    overhangMaterial.dispose();
    sunMarker.geometry.dispose();
    sunMarker.material.dispose();
    moonMarker.geometry.dispose();
    moonMarker.material.dispose();
    renderer.shadowMap.enabled = previousShadow.enabled;
    renderer.shadowMap.type = previousShadow.type;
    renderer.shadowMap.autoUpdate = previousShadow.autoUpdate;
    setDebugValue(modelId, null);
  }

  return { frame, refresh, invalidate, isEnabled: () => enabled, dispose };
}

export interface SolarStudyPublicApi {
  version: string;
  getState(): SolarStudyState;
  subscribe(callback: SolarStudySubscriber): () => void;
  patchState(patch: SolarStudyPatch): SolarStudyState;
  setEnabled(enabled: boolean): SolarStudyState;
  setLocation(location: SolarLocation): SolarStudyState;
  setSeason(season: SolarSeason): SolarStudyState;
  setDate(date: string): SolarStudyState;
  setMinuteIndex(minuteIndex: number): SolarStudyState;
  setPlaying(playing: boolean): SolarStudyState;
  togglePlaying(): SolarStudyState;
  setFacadeBearing(facadeBearing: number): SolarStudyState;
  setOverhangM(overhangM: number): SolarStudyState;
  searchLocations: typeof searchLocations;
}

export const solarStudyApi: SolarStudyPublicApi = Object.freeze({
  version: "1.0.0",
  getState: solarStudy.getState,
  subscribe: solarStudy.subscribe,
  patchState: solarStudy.patchState,
  setEnabled: solarStudy.setEnabled,
  setLocation: solarStudy.setLocation,
  setSeason: solarStudy.setSeason,
  setDate: solarStudy.setDate,
  setMinuteIndex: solarStudy.setMinuteIndex,
  setPlaying: solarStudy.setPlaying,
  togglePlaying: solarStudy.togglePlaying,
  setFacadeBearing: solarStudy.setFacadeBearing,
  setOverhangM: solarStudy.setOverhangM,
  searchLocations,
});

declare global {
  interface Window {
    __quonsetSolarStudy?: SolarStudyPublicApi;
    __quonsetSolarScenes?: Record<string, Record<string, unknown>>;
    __qr3dSolar?: Record<string, unknown>;
    __qr3d2brSolar?: Record<string, unknown>;
  }
}

if (typeof window !== "undefined") {
  window.__quonsetSolarStudy = solarStudyApi;
  queueMicrotask(() => window.dispatchEvent(new CustomEvent("quonset-solar-ready", {
    detail: solarStudyApi,
  })));
}
